/*
 * RESET PASSWORD (decision 607): the page a reset link opens.
 *
 * NOTHING HAPPENS UNTIL THE FORM IS SUBMITTED. The link is a GET of this static
 * page, and mail scanners prefetch links, so the token is only ever spent by the
 * POST this form makes.
 *
 * The form is SignIn's create-account shape on purpose: `new-password` on both
 * fields so a password manager offers to generate and then save, the twelve-
 * character floor on the first field only, and the mismatch refused before any
 * request (decision 385's rules). On success the server has ended every other
 * session and signed THIS device in, so the page hands the user up to the app
 * and goes to "/" for the landing decision, exactly as a sign-in does.
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { Brand } from "../components/Brand";
import { ApiError, api, type User } from "../lib/api";
import { useLinkToken } from "../lib/linkToken";
import styles from "./SignIn.module.css";

export function ResetPassword({ onSignedIn }: { onSignedIn: (user: User) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useLinkToken();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState(token === null);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMismatch(false);
    if (password !== confirmPassword) {
      setMismatch(true);
      return;
    }
    if (!token) return;

    setBusy(true);
    try {
      const body = await api.confirmPasswordReset(token, password);
      onSignedIn(body.user as User);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.failure.kind === "server" && err.failure.status === 400) {
        setInvalid(true);
      } else {
        setError(
          err instanceof ApiError && err.failure.kind === "offline"
            ? t("auth.errorOffline")
            : t("auth.errorGeneric"),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Brand variant="lockup" height={34} />
        </div>
        <h1 className={styles.heading}>{t("auth.reset.heading")}</h1>

        {invalid ? (
          <>
            <p className={styles.intro}>{t("auth.linkInvalid")}</p>
            <p className={styles.alt}>
              <Link to="/forgot-password">{t("auth.reset.askAgain")}</Link>
            </p>
          </>
        ) : (
          <>
            <p className={styles.intro}>{t("auth.reset.intro")}</p>
            <form className={styles.form} onSubmit={(e) => void submit(e)}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="new-password">
                  {t("settings.newPassword")}
                </label>
                <input
                  id="new-password"
                  className={styles.input}
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  aria-describedby="new-password-hint"
                  required
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <span id="new-password-hint" className={styles.hint}>
                  {t("auth.passwordHint")}
                </span>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="confirm-password">
                  {t("settings.confirmPassword")}
                </label>
                <input
                  id="confirm-password"
                  className={styles.input}
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  {...(mismatch ? { "aria-describedby": "confirm-password-mismatch" } : {})}
                  required
                  // No minLength, as on every confirm field (decision 385).
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setMismatch(false);
                  }}
                />
                {mismatch ? (
                  <span id="confirm-password-mismatch" className={styles.error}>
                    {t("auth.passwordMismatch")}
                  </span>
                ) : null}
              </div>
              {error ? (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              ) : null}
              <button className={styles.primary} type="submit" disabled={busy}>
                {t("auth.reset.action")}
              </button>
            </form>
          </>
        )}

        <p className={styles.alt}>
          <Link to="/">{t("auth.backToSignIn")}</Link>
        </p>
      </div>
    </div>
  );
}
